import { FocusTrap } from 'focus-trap-react'
import { useContext, useCallback, useEffect, useState, useMemo } from 'react'
import type { Book as BookType, Recipient } from 'src/subscribe'
import { Context } from 'src/Subscribe_'
import Book from 'src/Subscribe_/Book'

const Recipient = ({
  recipient,
  startableBooks,
}: {
  recipient: Recipient
  startableBooks: BookType[]
}) => {
  const { dispatch } = useContext(Context)

  const [open, setOpen] = useState<boolean>(false)
  const [removeModalVisible, setRemoveModalVisible] = useState<boolean>(false)

  const handleToggleClick = useCallback(() => {
    setOpen((currentOpen) => !currentOpen)
  }, [])

  const handleRemoveClick = useCallback(() => {
    setRemoveModalVisible(true)
  }, [])

  const handleEditClick = useCallback(() => {
    if (recipient.email) {
      dispatch({
        payload: {
          recipient,
        },
        type: 'edit recipient clicked',
      })
    }
  }, [])

  const handleRemoveConfirm = useCallback(() => {
    if (recipient.email) {
      dispatch({
        payload: {
          recipient,
        },
        type: 'remove recipient confirmed',
      })
    }
  }, [])

  const handleRemoveCancel = useCallback(() => {
    setRemoveModalVisible(false)
  }, [])

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setRemoveModalVisible(false)
      }
    }

    if (removeModalVisible) {
      document.addEventListener('keydown', handleKeydown)
    } else {
      document.removeEventListener('keydown', handleKeydown)
    }
  }, [removeModalVisible])

  const book = useMemo(() => {
    for (const sb of startableBooks) {
      if (recipient.starting_book_number === sb.number) {
        return sb
      }
    }
    return startableBooks[0]!
  }, [recipient.starting_book_number, startableBooks])

  return (
    <div className="recipient">
      <button className="top" onClick={handleToggleClick} type="button">
        <div className="name">{recipient.name}</div>
        <div className="toggle">
          {!open && (
            <svg
              fill="none"
              viewBox="0 0 17 9"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.33333 8.30542L0 0H16.6667L8.33333 8.30542Z"
                fill="currentColor"
              />
            </svg>
          )}
          {open && (
            <svg
              fill="none"
              viewBox="0 0 17 9"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.33366 0.000245319L16.667 8.30566L0.000324249 8.30566L8.33366 0.000245319Z"
                fill="currentColor"
              />
            </svg>
          )}
        </div>
      </button>
      {open && (
        <>
          <div className="bottom">
            <div>{recipient.email}</div>
            {recipient.address?.organization && (
              <div>{recipient.address?.organization}</div>
            )}
            <div>{recipient.address?.address_line1}</div>
            {recipient.address?.address_line2 && (
              <div>{recipient.address.address_line2}</div>
            )}
            <div>
              {recipient.address?.locality},{' '}
              {recipient.address?.administrative_area}{' '}
              {recipient.address?.postal_code}
            </div>
            <div>{recipient.phone}</div>
            {recipient.starting_book_number && (
              <div>
                <span>Subscription starts with </span>
                <Book book={book} />
              </div>
            )}
          </div>
          <div className="actions">
            <button onClick={handleRemoveClick} type="button">
              Remove
            </button>
            <button onClick={handleEditClick} type="button">
              Edit
            </button>
          </div>
        </>
      )}
      {removeModalVisible && (
        <FocusTrap>
          <div className="overlay enabled active" id="remove-modal">
            <div className="block__content">
              <div>
                <div className="info">
                  <p>Remove recipient from the list?</p>
                </div>
                <div className="actions">
                  <button onClick={handleRemoveConfirm} type="button">
                    Confirm
                  </button>
                  <button onClick={handleRemoveCancel} type="button">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </FocusTrap>
      )}
    </div>
  )
}

export default Recipient
